import { queryOptions, useQuery } from "@tanstack/react-query";

import makes from "../../assets/mocks/makes.json";
import data from "../../assets/mocks/market-cars.json";
import { QueryParams } from "./types";
import { getDataFilteredAndSorted } from "./utils";

import { env } from "@/config/env";
import { api } from "@/lib/api-client";
import { hasAccessToken } from "@/lib/auth";
import { AxiosMock } from "@/utils/mock";

import type { QueryConfig } from "@/lib/react-query";
import type { PaginatedList } from "@/types/entity";
import type { MarketCar } from "@/types/market";

if (import.meta.env.MODE === "test") {
  // Mocking the API response for watchlists
  const mock = AxiosMock.getInstance();
  makes.forEach((make) => {
    mock.onGet(`/carburate/market/makes/${make.id}/cars/?page=1&page_size=25`).reply(200, data);
  });
}

type MakeQueryParams = QueryParams & {
  makeId: string;
};

export const getMarketMakeCars = ({ makeId, page, pageSize }: MakeQueryParams) => {
  return api.get(
    `/carburate/market/makes/${makeId}/cars/?page=${page}&page_size=${pageSize}`
  ) as Promise<PaginatedList<MarketCar>>;
};

export const getMarketMakeCarsQueryOptions = ({ makeId, page, pageSize }: MakeQueryParams) => {
  return queryOptions({
    queryKey: ["market", "makes", makeId, "cars", { page, pageSize }],
    queryFn: () => getMarketMakeCars({ makeId, page, pageSize }),
  });
};

type UseMarketMakeCarsOptions = MakeQueryParams & {
  enabled?: boolean;
  queryConfig?: QueryConfig<typeof getMarketMakeCars>;
};

export const useMarketMakeCars = ({
  makeId,
  parameters,
  enabled = true,
  queryConfig,
  page,
  pageSize,
  sort,
}: UseMarketMakeCarsOptions) => {
  const isClientPagination = env.PAGINATION === "client";

  const currentPage = isClientPagination ? 1 : page;
  const currentPageSize = isClientPagination ? 999999 : pageSize;

  let data: PaginatedList<MarketCar>;

  const {
    data: response,
    isSuccess,
    ...props
  } = useQuery({
    enabled: hasAccessToken() && !!makeId && enabled,
    ...getMarketMakeCarsQueryOptions({ makeId, page: currentPage, pageSize: currentPageSize }),
    ...queryConfig,
  });

  data = response!;
  if (isClientPagination && isSuccess) {
    data = getDataFilteredAndSorted(data, { parameters, page, pageSize, sort });
  }

  return { data, isSuccess, ...props };
};
